/**
 * MentionDropdown — the `@`-mention autocomplete list rendered above the chat
 * composer.
 *
 * Presentational sibling of the inline slash-command dropdown in ChatComposer:
 * same anchoring (`position: absolute; bottom: 100%`), same selected-row
 * affordance, click-to-select + hover-to-highlight. All state (visibility,
 * rows, highlighted index, loading) is owned by `useMentionAutocomplete` and
 * threaded in; this component is pure.
 *
 * Each row is a workspace object keyed to `SearchResultObject` ({id,type,title}).
 * Selecting a row splices the canonical `@[Title](type:id)` marker into the
 * textarea (handled by the parent's `onSelect`).
 */
import type { MentionCandidate } from '@/features/chat/hooks/useMentionAutocomplete';

export interface MentionDropdownProps {
  show: boolean;
  matches: MentionCandidate[];
  selectedIndex: number;
  isLoading: boolean;
  onSelect: (index: number) => void;
  onHighlight: (index: number) => void;
}

/** A short, human-friendly label for an object type (contact/task/event/…). */
function typeLabel(type: string): string {
  if (!type) return '';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function MentionDropdown({
  show,
  matches,
  selectedIndex,
  isLoading,
  onSelect,
  onHighlight,
}: MentionDropdownProps) {
  if (!show) return null;

  return (
    <div className="mention-autocomplete-dropdown" role="listbox" aria-label="Mention suggestions">
      {isLoading && matches.length === 0 && (
        <div className="mention-autocomplete-loading" aria-live="polite">
          Searching…
        </div>
      )}
      {!isLoading && matches.length === 0 && (
        <div className="mention-autocomplete-empty">No matches</div>
      )}
      {matches.map((item, index) => (
        <button
          key={`${item.type}:${item.id}`}
          type="button"
          role="option"
          aria-selected={index === selectedIndex}
          className={`mention-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
          // Use onMouseDown (not onClick) so the textarea doesn't blur/close the
          // dropdown before the selection fires.
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(index);
          }}
          onMouseEnter={() => onHighlight(index)}
        >
          <span className="mention-item-title">{item.title || 'Untitled'}</span>
          <span className="mention-item-type">{typeLabel(item.type)}</span>
        </button>
      ))}
    </div>
  );
}
